import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Button, Modal, Field, Input, Select, StatusPill, Spinner } from "./ui";
import { SCHEMAS, MONEY_KEYS, STATUS_KEYS, displayName } from "../lib/schemas";
import { formatEUR, formatDate, cn } from "../lib/utils";
import { Plus, Pencil, Trash2, Search, Repeat, Check } from "lucide-react";

function lstr(obj, lang) {
  if (!obj) return "";
  return obj[lang] ?? obj.fr ?? "";
}

export default function DataTable({ resource, entityId, defaultValues = {}, compact = false }) {
  const { t, lang, entities } = useApp();
  const schema = SCHEMAS[resource];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refMaps, setRefMaps] = useState({});
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = entityId ? `/data/${resource}?entity_id=${entityId}` : `/data/${resource}`;
      const { data } = await api.get(url);
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [resource, entityId]);

  const loadRefs = useCallback(async () => {
    const refs = [...new Set(schema.fields.filter((f) => f.type === "ref").map((f) => f.ref))];
    const maps = {};
    for (const r of refs) {
      if (r === "entities") {
        maps[r] = entities.map((e) => ({ id: e.id, name: e.name }));
      } else if (r === "space_projects") {
        // Real projects live in Spaces, scoped to the entity.
        try {
          const { data } = await api.get(`/space-projects${entityId ? `?entity_id=${entityId}` : ""}`);
          maps[r] = data;
        } catch {
          maps[r] = [];
        }
      } else {
        try {
          const { data } = await api.get(`/data/${r}`);
          maps[r] = data;
        } catch {
          maps[r] = [];
        }
      }
    }
    setRefMaps(maps);
  }, [schema, entities, entityId]);

  useEffect(() => { load(); loadRefs(); }, [load, loadRefs]);

  const refName = (ref, id) => {
    if (!id) return "—";
    const list = refMaps[ref] || [];
    const item = list.find((x) => x.id === id);
    return item ? (item.name || displayName(ref, item)) : "—";
  };

  const openCreate = () => {
    const init = { ...defaultValues };
    schema.fields.forEach((f) => {
      if (init[f.key] === undefined) init[f.key] = f.type === "multiselect" ? [] : "";
    });
    setForm(init);
    setEditing("new");
  };

  const openEdit = (row) => {
    const init = {};
    schema.fields.forEach((f) => {
      init[f.key] = row[f.key] ?? (f.type === "multiselect" ? [] : "");
    });
    setForm({ ...init, id: row.id });
    setEditing(row);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      schema.fields.forEach((f) => {
        if ((f.type === "number" || f.type === "money") && payload[f.key] !== "" && payload[f.key] !== null) {
          payload[f.key] = Number(payload[f.key]);
        }
      });
      if (entityId) payload.entity_id = entityId;
      else if (defaultValues.entity_id) payload.entity_id = defaultValues.entity_id;

      if (editing === "new") {
        await api.post(`/data/${resource}`, payload);
      } else {
        await api.put(`/data/${resource}/${form.id}`, payload);
      }
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/data/${resource}/${row.id}`);
    await load();
  };

  const isTasks = resource === "tasks";

  const toggleComplete = async (row) => {
    const newStatus = row.status === "done" ? "open" : "done";
    await api.put(`/data/tasks/${row.id}`, { status: newStatus });
    await load();
  };

  const renderCell = (row, key) => {
    const field = schema.fields.find((f) => f.key === key);
    const val = row[key];
    if (key === "recurrence") {
      if (!val || val === "none") return <span className="text-text-secondary">—</span>;
      const opt = field?.options?.find((o) => o.value === val);
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "#60A5FA" }}>
          <Repeat className="w-3.5 h-3.5" /> {opt ? lstr(opt.label, lang) : val}
        </span>
      );
    }
    if (field?.type === "ref") return refName(field.ref, val);
    if (STATUS_KEYS.has(key) && val) {
      const opt = field?.options?.find((o) => o.value === val);
      return <StatusPill status={val} label={opt ? lstr(opt.label, lang) : val} />;
    }
    if (MONEY_KEYS.has(key)) return <span className="tabular-nums">{formatEUR(val)}</span>;
    if (field?.type === "date") return formatDate(val, lang === "fr" ? "fr-FR" : "en-US");
    if (key === "roles" && Array.isArray(val)) {
      return (
        <span className="flex flex-wrap gap-1">
          {val.map((r) => (
            <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-hover text-text-secondary capitalize">{r}</span>
          ))}
        </span>
      );
    }
    if (field?.type === "number") return <span className="tabular-nums">{val ?? "—"}</span>;
    if (key === "change_pct") return <span className="tabular-nums">{val}%</span>;
    return val || "—";
  };

  const filtered = rows.filter((r) => {
    if (!query) return true;
    const hay = Object.values(r).join(" ").toLowerCase();
    return hay.includes(query.toLowerCase());
  });

  const headerLabel = (key) => {
    const f = schema.fields.find((x) => x.key === key);
    return f ? lstr(f.label, lang) : key;
  };

  return (
    <Card className={cn("!p-0 overflow-hidden", compact && "")} data-testid={`table-${resource}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search")}
            data-testid={`search-${resource}`}
            className="w-full rounded-lg border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-[var(--border-strong)]"
          />
        </div>
        <Button size="sm" onClick={openCreate} data-testid={`add-${resource}`}>
          <Plus className="w-3.5 h-3.5" /> {t("add")}
        </Button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-text-secondary">{t("no_data")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                {schema.columns.map((c) => (
                  <th key={c} className="px-5 py-2.5 text-[11px] tracking-wider uppercase font-semibold text-text-secondary whitespace-nowrap">
                    {headerLabel(c)}
                  </th>
                ))}
                <th className="px-5 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-b last:border-0 hover:bg-surface-hover transition-colors" data-testid={`row-${resource}-${row.id}`}>
                  {schema.columns.map((c) => (
                    <td key={c} className="px-5 py-3 align-middle">{renderCell(row, c)}</td>
                  ))}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(row)} data-testid={`edit-${row.id}`} className="p-1.5 rounded-md text-text-secondary hover:text-text-primary hover:bg-background transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(row)} data-testid={`delete-${row.id}`} className="p-1.5 rounded-md text-text-secondary hover:text-red-400 hover:bg-background transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === "new" ? t("add") : t("edit")}
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => setEditing(null)}>{t("cancel")}</Button>
            <Button size="md" onClick={save} disabled={saving} data-testid="form-save">
              {saving && <Spinner className="w-3.5 h-3.5" />} {t("save")}
            </Button>
          </>
        }
      >
        {editing && schema.fields.map((f) => {
          if (entityId && f.key === "entity_id") return null;
          return (
            <Field key={f.key} label={lstr(f.label, lang) + (f.required ? " *" : "")}>
              {f.type === "textarea" ? (
                <textarea
                  rows={2}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-[var(--border-strong)] resize-none"
                />
              ) : f.type === "select" ? (
                <Select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`}>
                  <option value="">{t("none")}</option>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{lstr(o.label, lang)}</option>)}
                </Select>
              ) : f.type === "ref" ? (
                <Select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`}>
                  <option value="">{t("none")}</option>
                  {(refMaps[f.ref] || []).map((o) => (
                    <option key={o.id} value={o.id}>{o.name || displayName(f.ref, o)}</option>
                  ))}
                </Select>
              ) : f.type === "multiselect" ? (
                <div className="flex flex-wrap gap-2">
                  {f.options.map((o) => {
                    const arr = form[f.key] || [];
                    const on = arr.includes(o.value);
                    return (
                      <button
                        type="button"
                        key={o.value}
                        data-testid={`field-${f.key}-${o.value}`}
                        onClick={() => setForm({ ...form, [f.key]: on ? arr.filter((x) => x !== o.value) : [...arr, o.value] })}
                        className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                          on ? "bg-text-primary text-background border-transparent" : "text-text-secondary hover:text-text-primary")}
                      >
                        {lstr(o.label, lang)}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <Input
                  type={f.type === "number" || f.type === "money" ? "number" : f.type === "date" ? "date" : "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                />
              )}
            </Field>
          );
        })}
      </Modal>
    </Card>
  );
}
